'use client'

import { Specialty } from '@/lib/api/common/interfaces/specialty.interface'
import {
    createEstablishment,
    ESTABLISHMENT_TYPE_LABELS,
    EstablishmentType,
    getEstablishment,
    updateEstablishment,
    uploadEstablishmentLogo,
} from '@/lib/api/establishments/establishments-api'
import { fetchPostalCode } from '@/lib/api/fetch-postal-code'
import { getMedicalServicesByOrganization, MedicalService } from '@/lib/api/medical-services/medical-services-api'
import { getAllWithSubspecialtiesByOrganization } from '@/lib/api/specialties/specialties-api'
import { useAuth } from '@/store/hooks'
import { useImageCrop } from '@/components/image-crop'
import { useEffect, useState } from 'react'
import {
    MdArrowBack,
    MdBusiness,
    MdClose,
    MdPhotoCamera,
    MdSave,
} from 'react-icons/md'
import { useNavigate, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'

const ESTABLISHMENT_TYPES: EstablishmentType[] = [
    'CLINIC', 'HOSPITAL', 'GYM', 'LABORATORY', 'PHARMACY',
    'REHABILITATION_CENTER', 'SPA', 'DENTAL_CLINIC', 'VETERINARY', 'OTHER',
]

const formatCnpj = (value: string) => {
    const digits = value.replace(/\D/g, '').slice(0, 14)
    return digits
        .replace(/^(\d{2})(\d)/, '$1.$2')
        .replace(/^(\d{2})\.(\d{3})(\d)/, '$1.$2.$3')
        .replace(/\.(\d{3})(\d)/, '.$1/$2')
        .replace(/(\d{4})(\d)/, '$1-$2')
}

const formatCep = (value: string) => {
    const digits = value.replace(/\D/g, '').slice(0, 8)
    return digits.replace(/^(\d{5})(\d)/, '$1-$2')
}

const formatPhone = (value: string) => {
    const digits = value.replace(/\D/g, '').slice(0, 11)
    if (digits.length <= 10) {
        return digits
            .replace(/^(\d{2})(\d)/, '($1) $2')
            .replace(/(\d{4})(\d)/, '$1-$2')
    }
    return digits
        .replace(/^(\d{2})(\d)/, '($1) $2')
        .replace(/(\d{5})(\d)/, '$1-$2')
}

export default function EstablishmentsFormPage() {
    const { user } = useAuth()
    const navigate = useNavigate()
    const { id } = useParams<{ id: string }>()
    const isEditing = !!id

    const [loading, setLoading] = useState(false)
    const [saving, setSaving] = useState(false)
    const [loadingCep, setLoadingCep] = useState(false)
    const [uploadingLogo, setUploadingLogo] = useState(false)
    // Logo de estabelecimento pode ir a documento impresso: PNG preserva a
    // transparência e o @react-pdf/renderer não lê WebP.
    const logoCrop = useImageCrop({
        title: 'Ajustar logo',
        output: { maxWidth: 512, mimeType: 'image/png' },
    })

    // Dados do formulario
    const [name, setName] = useState('')
    const [tradingName, setTradingName] = useState('')
    const [cnpj, setCnpj] = useState('')
    const [type, setType] = useState<EstablishmentType>('CLINIC')
    const [email, setEmail] = useState('')
    const [phone1, setPhone1] = useState('')
    const [phone2, setPhone2] = useState('')
    const [phone3, setPhone3] = useState('')
    const [notes, setNotes] = useState('')
    const [street, setStreet] = useState('')
    const [number, setNumber] = useState('')
    const [complement, setComplement] = useState('')
    const [neighborhood, setNeighborhood] = useState('')
    const [city, setCity] = useState('')
    const [state, setState] = useState('')
    const [zipCode, setZipCode] = useState('')
    const [active, setActive] = useState(true)
    const [logoUrl, setLogoUrl] = useState<string | null>(null)
    const [logoPreview, setLogoPreview] = useState<string | null>(null)
    const [logoFile, setLogoFile] = useState<File | null>(null)

    // Especialidades e Servicos
    const [availableSpecialties, setAvailableSpecialties] = useState<Specialty[]>([])
    const [availableServices, setAvailableServices] = useState<MedicalService[]>([])
    const [selectedSpecialtyIds, setSelectedSpecialtyIds] = useState<number[]>([])
    const [selectedServiceIds, setSelectedServiceIds] = useState<number[]>([])
    const [specialtySearch, setSpecialtySearch] = useState('')
    const [subspecialtySearch, setSubspecialtySearch] = useState('')
    const [serviceSearch, setServiceSearch] = useState('')
    const [showSpecialtyDropdown, setShowSpecialtyDropdown] = useState(false)
    const [showSubspecialtyDropdown, setShowSubspecialtyDropdown] = useState(false)
    const [showServiceDropdown, setShowServiceDropdown] = useState(false)

    useEffect(() => {
        if (user?.organizationId) {
            loadOptions()
        }
    }, [user?.organizationId])

    useEffect(() => {
        if (isEditing && id) {
            loadEstablishment()
        }
    }, [id, isEditing])

    const loadOptions = async () => {
        if (!user?.organizationId) return
        const [specRes, svcRes] = await Promise.all([
            getAllWithSubspecialtiesByOrganization(user.organizationId),
            getMedicalServicesByOrganization(user.organizationId, { activeOnly: true }),
        ])
        if (specRes.success) setAvailableSpecialties(specRes.result)
        if (svcRes.success) setAvailableServices(svcRes.result)
    }

    const loadEstablishment = async () => {
        if (!id) return
        setLoading(true)
        try {
            const res = await getEstablishment(parseInt(id))
            if (res.success) {
                const item = res.result
                setName(item.name)
                setTradingName(item.tradingName || '')
                setCnpj(item.cnpj ? formatCnpj(item.cnpj) : '')
                setType(item.type)
                setEmail(item.email || '')
                setPhone1(item.phone1 ? formatPhone(item.phone1) : '')
                setPhone2(item.phone2 ? formatPhone(item.phone2) : '')
                setPhone3(item.phone3 ? formatPhone(item.phone3) : '')
                setNotes(item.notes || '')
                setStreet(item.street || '')
                setNumber(item.number || '')
                setComplement(item.complement || '')
                setNeighborhood(item.neighborhood || '')
                setCity(item.city || '')
                setState(item.state || '')
                setZipCode(item.zipCode ? formatCep(item.zipCode) : '')
                setActive(item.active)
                setLogoUrl(item.logoUrl || null)
                setSelectedSpecialtyIds(item.specialties.map(s => s.specialty.id))
                setSelectedServiceIds(item.services.map(s => s.medicalService.id))
            } else {
                toast.error('Erro ao carregar estabelecimento')
                navigate('/registrations/establishments')
            }
        } catch {
            toast.error('Erro ao carregar estabelecimento')
            navigate('/registrations/establishments')
        } finally {
            setLoading(false)
        }
    }

    const handleZipCodeChange = async (value: string) => {
        const formatted = formatCep(value)
        setZipCode(formatted)

        const cleanZip = value.replace(/\D/g, '')
        if (cleanZip.length === 8) {
            setLoadingCep(true)
            try {
                const data = await fetchPostalCode(cleanZip)
                if (data && !data.erro) {
                    setStreet(data.logradouro || street)
                    setNeighborhood(data.bairro || neighborhood)
                    setCity(data.localidade || city)
                    setState(data.uf || state)
                    setComplement(data.complemento || complement)
                }
            } catch {
                // CEP nao encontrado
            } finally {
                setLoadingCep(false)
            }
        }
    }

    const handleLogoChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0]
        e.target.value = ''
        if (!file) return

        if (!file.type.startsWith('image/')) {
            toast.error('Apenas imagens são permitidas')
            return
        }
        if (file.size > 25 * 1024 * 1024) {
            toast.error('Tamanho máximo: 25MB')
            return
        }

        const cropped = await logoCrop.cropFile(file)
        if (!cropped) return
        setLogoFile(cropped)
        setLogoPreview(URL.createObjectURL(cropped))
    }

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()

        if (!name.trim()) {
            toast.error('Razão Social é obrigatória')
            return
        }

        setSaving(true)
        try {
            const cleanCnpj = cnpj.replace(/\D/g, '') || undefined
            const cleanPhone1 = phone1.replace(/\D/g, '') || undefined
            const cleanPhone2 = phone2.replace(/\D/g, '') || undefined
            const cleanPhone3 = phone3.replace(/\D/g, '') || undefined
            const cleanZip = zipCode.replace(/\D/g, '') || undefined

            const data: any = {
                name: name.trim(),
                tradingName: tradingName.trim() || undefined,
                cnpj: cleanCnpj,
                type,
                email: email.trim() || undefined,
                phone1: cleanPhone1,
                phone2: cleanPhone2,
                phone3: cleanPhone3,
                notes: notes.trim() || undefined,
                street: street.trim() || undefined,
                number: number.trim() || undefined,
                complement: complement.trim() || undefined,
                neighborhood: neighborhood.trim() || undefined,
                city: city.trim() || undefined,
                state: state.trim() || undefined,
                zipCode: cleanZip,
                active,
                organizationId: user!.organizationId!,
                specialtyIds: selectedSpecialtyIds,
                serviceIds: selectedServiceIds,
            }

            const response = isEditing
                ? await updateEstablishment(parseInt(id!), data)
                : await createEstablishment(data)

            if (response.success) {
                // Upload logo se houver arquivo novo
                if (logoFile && response.result?.id) {
                    setUploadingLogo(true)
                    try {
                        await uploadEstablishmentLogo(response.result.id, logoFile)
                    } catch {
                        toast.warning('Estabelecimento salvo, mas erro ao enviar logo')
                    } finally {
                        setUploadingLogo(false)
                    }
                }

                toast.success(isEditing ? 'Estabelecimento atualizado com sucesso' : 'Estabelecimento criado com sucesso')
                navigate('/registrations/establishments')
            } else {
                toast.error(response.error || 'Erro ao salvar estabelecimento')
            }
        } catch {
            toast.error('Erro ao salvar estabelecimento')
        } finally {
            setSaving(false)
        }
    }

    const addSpecialty = (spec: Specialty) => {
        if (!selectedSpecialtyIds.includes(spec.id)) {
            setSelectedSpecialtyIds(prev => [...prev, spec.id])
        }
        setSpecialtySearch('')
        setSubspecialtySearch('')
        setShowSpecialtyDropdown(false)
        setShowSubspecialtyDropdown(false)
    }

    const removeSpecialty = (specId: number) => {
        setSelectedSpecialtyIds(prev => prev.filter(id => id !== specId))
    }

    const addService = (svc: MedicalService) => {
        if (!selectedServiceIds.includes(svc.id)) {
            setSelectedServiceIds(prev => [...prev, svc.id])
        }
        setServiceSearch('')
        setShowServiceDropdown(false)
    }

    const removeService = (svcId: number) => {
        setSelectedServiceIds(prev => prev.filter(id => id !== svcId))
    }

    // Separar especialidades pai e subs
    const parentSpecialties = availableSpecialties.filter(s => !s.parentId)
    const allSubspecialties = availableSpecialties.filter(s => !!s.parentId)

    // Filtrar por busca e excluir ja selecionadas
    const filteredSpecialties = parentSpecialties.filter(s =>
        !selectedSpecialtyIds.includes(s.id) &&
        s.name.toLowerCase().includes(specialtySearch.toLowerCase().trim())
    )

    const filteredSubspecialties = allSubspecialties.filter(s =>
        !selectedSpecialtyIds.includes(s.id) &&
        (s.name.toLowerCase().includes(subspecialtySearch.toLowerCase().trim()) ||
         s.parent?.name?.toLowerCase().includes(subspecialtySearch.toLowerCase().trim()))
    )

    const filteredServices = availableServices.filter(s =>
        !selectedServiceIds.includes(s.id) &&
        s.name.toLowerCase().includes(serviceSearch.toLowerCase().trim())
    )

    // Obter objetos selecionados para exibir chips
    const selectedSpecialties = availableSpecialties.filter(s => selectedSpecialtyIds.includes(s.id))
    const selectedServices = availableServices.filter(s => selectedServiceIds.includes(s.id))
    const selectedParentSpecs = selectedSpecialties.filter(s => !s.parentId)
    const selectedSubSpecs = selectedSpecialties.filter(s => !!s.parentId)

    if (loading) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
                <div className="loading loading-spinner loading-lg text-secondary"></div>
                <p className="text-base-content/60 text-sm">Carregando...</p>
            </div>
        )
    }

    return (
        <div className="flex flex-col gap-6 max-w-4xl mx-auto">
            <div className="flex items-center gap-3">
                <button
                    onClick={() => navigate('/registrations/establishments')}
                    className="btn btn-ghost btn-sm btn-circle"
                >
                    <MdArrowBack className="text-xl" />
                </button>
                <h1 className="sm:text-lg lg:text-2xl font-bold text-base-content">
                    {isEditing ? 'Editar Estabelecimento' : 'Novo Estabelecimento'}
                </h1>
            </div>

            <form onSubmit={handleSubmit} className="flex flex-col gap-6">
                {/* Logo */}
                <div className="bg-base-100 rounded-xl border border-base-300/50 shadow-sm p-6">
                    <h2 className="text-lg font-semibold text-base-content mb-4">Logo</h2>
                    <div className="flex items-center gap-4">
                        <div className="relative">
                            {logoPreview || logoUrl ? (
                                <img
                                    src={logoPreview || logoUrl!}
                                    alt="Logo"
                                    className="w-24 h-24 rounded-xl object-cover border border-base-300/50"
                                />
                            ) : (
                                <div className="w-24 h-24 rounded-xl bg-base-200 flex items-center justify-center">
                                    <MdBusiness className="text-3xl text-base-content/30" />
                                </div>
                            )}
                            {logoPreview && (
                                <button
                                    type="button"
                                    onClick={() => { setLogoPreview(null); setLogoFile(null) }}
                                    className="absolute -top-2 -right-2 btn btn-circle btn-xs btn-error"
                                >
                                    <MdClose className="text-xs" />
                                </button>
                            )}
                        </div>
                        <div>
                            <label className="btn btn-outline btn-sm gap-2 cursor-pointer">
                                <MdPhotoCamera className="text-lg" />
                                {logoUrl || logoPreview ? 'Alterar' : 'Enviar logo'}
                                <input
                                    type="file"
                                    accept="image/*"
                                    className="hidden"
                                    onChange={handleLogoChange}
                                />
                            </label>
                            <p className="text-xs text-base-content/50 mt-1">PNG, JPG ou WEBP. A logo é cortada e comprimida ao escolher.</p>
                        </div>
                    </div>
                </div>

                {/* Dados Cadastrais */}
                <div className="bg-base-100 rounded-xl border border-base-300/50 shadow-sm p-6">
                    <h2 className="text-lg font-semibold text-base-content mb-4">Dados Cadastrais</h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="form-control">
                            <label className="label">
                                <span className="label-text font-medium">Razão Social *</span>
                            </label>
                            <input
                                type="text"
                                className="input input-bordered"
                                value={name}
                                onChange={e => setName(e.target.value)}
                                placeholder="Razão Social da empresa"
                                required
                            />
                        </div>
                        <div className="form-control">
                            <label className="label">
                                <span className="label-text font-medium">Nome Fantasia</span>
                            </label>
                            <input
                                type="text"
                                className="input input-bordered"
                                value={tradingName}
                                onChange={e => setTradingName(e.target.value)}
                                placeholder="Nome Fantasia"
                            />
                        </div>
                        <div className="form-control">
                            <label className="label">
                                <span className="label-text font-medium">CNPJ</span>
                            </label>
                            <input
                                type="text"
                                className="input input-bordered"
                                value={cnpj}
                                onChange={e => setCnpj(formatCnpj(e.target.value))}
                                placeholder="00.000.000/0000-00"
                            />
                        </div>
                        <div className="form-control">
                            <label className="label">
                                <span className="label-text font-medium">Tipo *</span>
                            </label>
                            <select
                                className="select select-bordered"
                                value={type}
                                onChange={e => setType(e.target.value as EstablishmentType)}
                                required
                            >
                                {ESTABLISHMENT_TYPES.map(t => (
                                    <option key={t} value={t}>{ESTABLISHMENT_TYPE_LABELS[t]}</option>
                                ))}
                            </select>
                        </div>
                        <div className="form-control">
                            <label className="label">
                                <span className="label-text font-medium">E-mail</span>
                            </label>
                            <input
                                type="email"
                                className="input input-bordered"
                                value={email}
                                onChange={e => setEmail(e.target.value)}
                                placeholder="contato@empresa.com"
                            />
                        </div>
                    </div>
                </div>

                {/* Telefones */}
                <div className="bg-base-100 rounded-xl border border-base-300/50 shadow-sm p-6">
                    <h2 className="text-lg font-semibold text-base-content mb-4">Telefones</h2>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div className="form-control">
                            <label className="label">
                                <span className="label-text font-medium">Telefone 1</span>
                            </label>
                            <input
                                type="text"
                                className="input input-bordered"
                                value={phone1}
                                onChange={e => setPhone1(formatPhone(e.target.value))}
                                placeholder="(00) 00000-0000"
                            />
                        </div>
                        <div className="form-control">
                            <label className="label">
                                <span className="label-text font-medium">Telefone 2</span>
                            </label>
                            <input
                                type="text"
                                className="input input-bordered"
                                value={phone2}
                                onChange={e => setPhone2(formatPhone(e.target.value))}
                                placeholder="(00) 00000-0000"
                            />
                        </div>
                        <div className="form-control">
                            <label className="label">
                                <span className="label-text font-medium">Telefone 3</span>
                            </label>
                            <input
                                type="text"
                                className="input input-bordered"
                                value={phone3}
                                onChange={e => setPhone3(formatPhone(e.target.value))}
                                placeholder="(00) 00000-0000"
                            />
                        </div>
                    </div>
                </div>

                {/* Endereço */}
                <div className="bg-base-100 rounded-xl border border-base-300/50 shadow-sm p-6">
                    <h2 className="text-lg font-semibold text-base-content mb-4">Endereço</h2>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div className="form-control">
                            <label className="label">
                                <span className="label-text font-medium">CEP</span>
                            </label>
                            <div className="relative">
                                <input
                                    type="text"
                                    className="input input-bordered w-full"
                                    value={zipCode}
                                    onChange={e => handleZipCodeChange(e.target.value)}
                                    placeholder="00000-000"
                                />
                                {loadingCep && (
                                    <span className="absolute right-3 top-1/2 -translate-y-1/2 loading loading-spinner loading-xs"></span>
                                )}
                            </div>
                        </div>
                        <div className="form-control md:col-span-2">
                            <label className="label">
                                <span className="label-text font-medium">Logradouro</span>
                            </label>
                            <input
                                type="text"
                                className="input input-bordered"
                                value={street}
                                onChange={e => setStreet(e.target.value)}
                                placeholder="Rua, Avenida..."
                            />
                        </div>
                        <div className="form-control">
                            <label className="label">
                                <span className="label-text font-medium">Número</span>
                            </label>
                            <input
                                type="text"
                                className="input input-bordered"
                                value={number}
                                onChange={e => setNumber(e.target.value)}
                                placeholder="123"
                            />
                        </div>
                        <div className="form-control">
                            <label className="label">
                                <span className="label-text font-medium">Complemento</span>
                            </label>
                            <input
                                type="text"
                                className="input input-bordered"
                                value={complement}
                                onChange={e => setComplement(e.target.value)}
                                placeholder="Sala, Andar..."
                            />
                        </div>
                        <div className="form-control">
                            <label className="label">
                                <span className="label-text font-medium">Bairro</span>
                            </label>
                            <input
                                type="text"
                                className="input input-bordered"
                                value={neighborhood}
                                onChange={e => setNeighborhood(e.target.value)}
                                placeholder="Bairro"
                            />
                        </div>
                        <div className="form-control">
                            <label className="label">
                                <span className="label-text font-medium">Cidade</span>
                            </label>
                            <input
                                type="text"
                                className="input input-bordered"
                                value={city}
                                onChange={e => setCity(e.target.value)}
                                placeholder="Cidade"
                            />
                        </div>
                        <div className="form-control">
                            <label className="label">
                                <span className="label-text font-medium">Estado</span>
                            </label>
                            <input
                                type="text"
                                className="input input-bordered"
                                value={state}
                                onChange={e => setState(e.target.value)}
                                placeholder="UF"
                                maxLength={2}
                            />
                        </div>
                    </div>
                </div>

                {/* Especialidades */}
                <div className="bg-base-100 rounded-xl border border-base-300/50 shadow-sm p-6">
                    <h2 className="text-lg font-semibold text-base-content mb-4">
                        Especialidades
                        {selectedParentSpecs.length > 0 && (
                            <span className="badge badge-secondary badge-sm ml-2">{selectedParentSpecs.length}</span>
                        )}
                    </h2>

                    <div className="relative">
                        <input
                            type="text"
                            className="input input-bordered w-full"
                            placeholder="Buscar especialidade..."
                            value={specialtySearch}
                            onChange={e => {
                                setSpecialtySearch(e.target.value)
                                setShowSpecialtyDropdown(true)
                            }}
                            onFocus={() => setShowSpecialtyDropdown(true)
                            }
                            onKeyDown={e => {
                                if (e.key === 'Escape') setShowSpecialtyDropdown(false)
                            }}
                        />
                        {showSpecialtyDropdown && specialtySearch.trim() && filteredSpecialties.length > 0 && (
                            <div className="absolute z-50 w-full mt-1 bg-base-100 border border-base-300 rounded-lg shadow-xl max-h-60 overflow-y-auto">
                                {filteredSpecialties.slice(0, 15).map(spec => (
                                    <button
                                        key={spec.id}
                                        type="button"
                                        className="w-full text-left px-4 py-2 hover:bg-base-200 border-b border-base-300/30 last:border-b-0 text-sm"
                                        onClick={() => addSpecialty(spec)}
                                    >
                                        {spec.name}
                                    </button>
                                ))}
                            </div>
                        )}
                        {showSpecialtyDropdown && specialtySearch.trim() && filteredSpecialties.length === 0 && (
                            <div className="absolute z-50 w-full mt-1 bg-base-100 border border-base-300 rounded-lg shadow-xl p-3">
                                <p className="text-sm text-base-content/60">Nenhuma especialidade encontrada</p>
                            </div>
                        )}
                    </div>

                    {selectedParentSpecs.length > 0 && (
                        <div className="flex flex-wrap gap-2 mt-3">
                            {selectedParentSpecs.map(spec => (
                                <span key={spec.id} className="badge badge-secondary gap-1 py-3">
                                    {spec.name}
                                    <button type="button" onClick={() => removeSpecialty(spec.id)}>
                                        <MdClose className="text-sm" />
                                    </button>
                                </span>
                            ))}
                        </div>
                    )}
                </div>

                {/* Subespecialidades */}
                <div className="bg-base-100 rounded-xl border border-base-300/50 shadow-sm p-6">
                    <h2 className="text-lg font-semibold text-base-content mb-4">
                        Subespecialidades
                        {selectedSubSpecs.length > 0 && (
                            <span className="badge badge-secondary badge-sm ml-2">{selectedSubSpecs.length}</span>
                        )}
                    </h2>

                    <div className="relative">
                        <input
                            type="text"
                            className="input input-bordered w-full"
                            placeholder="Buscar subespecialidade..."
                            value={subspecialtySearch}
                            onChange={e => {
                                setSubspecialtySearch(e.target.value)
                                setShowSubspecialtyDropdown(true)
                            }}
                            onFocus={() => setShowSubspecialtyDropdown(true)}
                            onKeyDown={e => {
                                if (e.key === 'Escape') setShowSubspecialtyDropdown(false)
                            }}
                        />
                        {showSubspecialtyDropdown && subspecialtySearch.trim() && filteredSubspecialties.length > 0 && (
                            <div className="absolute z-50 w-full mt-1 bg-base-100 border border-base-300 rounded-lg shadow-xl max-h-60 overflow-y-auto">
                                {filteredSubspecialties.slice(0, 15).map(sub => (
                                    <button
                                        key={sub.id}
                                        type="button"
                                        className="w-full text-left px-4 py-2 hover:bg-base-200 border-b border-base-300/30 last:border-b-0"
                                        onClick={() => addSpecialty(sub)}
                                    >
                                        <span className="text-sm font-medium">{sub.name}</span>
                                        {sub.parent && (
                                            <span className="text-xs text-base-content/50 ml-2">
                                                ({sub.parent.name})
                                            </span>
                                        )}
                                    </button>
                                ))}
                            </div>
                        )}
                        {showSubspecialtyDropdown && subspecialtySearch.trim() && filteredSubspecialties.length === 0 && (
                            <div className="absolute z-50 w-full mt-1 bg-base-100 border border-base-300 rounded-lg shadow-xl p-3">
                                <p className="text-sm text-base-content/60">Nenhuma subespecialidade encontrada</p>
                            </div>
                        )}
                    </div>

                    {selectedSubSpecs.length > 0 && (
                        <div className="flex flex-wrap gap-2 mt-3">
                            {selectedSubSpecs.map(sub => (
                                <span key={sub.id} className="badge badge-secondary gap-1 py-3">
                                    {sub.name}
                                    {sub.parent && (
                                        <span className="opacity-70 text-xs">({sub.parent.name})</span>
                                    )}
                                    <button type="button" onClick={() => removeSpecialty(sub.id)}>
                                        <MdClose className="text-sm" />
                                    </button>
                                </span>
                            ))}
                        </div>
                    )}
                </div>

                {/* Serviços */}
                <div className="bg-base-100 rounded-xl border border-base-300/50 shadow-sm p-6">
                    <h2 className="text-lg font-semibold text-base-content mb-4">
                        Serviços
                        {selectedServices.length > 0 && (
                            <span className="badge badge-secondary badge-sm ml-2">{selectedServices.length}</span>
                        )}
                    </h2>

                    <div className="relative">
                        <input
                            type="text"
                            className="input input-bordered w-full"
                            placeholder="Buscar serviço..."
                            value={serviceSearch}
                            onChange={e => {
                                setServiceSearch(e.target.value)
                                setShowServiceDropdown(true)
                            }}
                            onFocus={() => setShowServiceDropdown(true)}
                            onKeyDown={e => {
                                if (e.key === 'Escape') setShowServiceDropdown(false)
                            }}
                        />
                        {showServiceDropdown && serviceSearch.trim() && filteredServices.length > 0 && (
                            <div className="absolute z-50 w-full mt-1 bg-base-100 border border-base-300 rounded-lg shadow-xl max-h-60 overflow-y-auto">
                                {filteredServices.slice(0, 15).map(svc => (
                                    <button
                                        key={svc.id}
                                        type="button"
                                        className="w-full text-left px-4 py-2 hover:bg-base-200 border-b border-base-300/30 last:border-b-0 text-sm"
                                        onClick={() => addService(svc)}
                                    >
                                        {svc.name}
                                    </button>
                                ))}
                            </div>
                        )}
                        {showServiceDropdown && serviceSearch.trim() && filteredServices.length === 0 && (
                            <div className="absolute z-50 w-full mt-1 bg-base-100 border border-base-300 rounded-lg shadow-xl p-3">
                                <p className="text-sm text-base-content/60">Nenhum serviço encontrado</p>
                            </div>
                        )}
                    </div>

                    {selectedServices.length > 0 && (
                        <div className="flex flex-wrap gap-2 mt-3">
                            {selectedServices.map(svc => (
                                <span key={svc.id} className="badge badge-secondary gap-1 py-3">
                                    {svc.name}
                                    <button type="button" onClick={() => removeService(svc.id)}>
                                        <MdClose className="text-sm" />
                                    </button>
                                </span>
                            ))}
                        </div>
                    )}
                </div>

                {/* Observações */}
                <div className="bg-base-100 rounded-xl border border-base-300/50 shadow-sm p-6">
                    <h2 className="text-lg font-semibold text-base-content mb-4">Observações</h2>
                    <div className="form-control">
                        <textarea
                            className="textarea textarea-bordered w-full"
                            value={notes}
                            onChange={e => setNotes(e.target.value)}
                            placeholder="Observações sobre o estabelecimento..."
                            rows={4}
                        />
                    </div>
                </div>

                {/* Status */}
                {isEditing && (
                    <div className="bg-base-100 rounded-xl border border-base-300/50 shadow-sm p-6">
                        <h2 className="text-lg font-semibold text-base-content mb-4">Status</h2>
                        <div className="form-control">
                            <label className="label cursor-pointer justify-start gap-3">
                                <input
                                    type="checkbox"
                                    className="checkbox checkbox-secondary"
                                    checked={active}
                                    onChange={e => setActive(e.target.checked)}
                                />
                                <span className="label-text font-medium">Ativo</span>
                            </label>
                        </div>
                    </div>
                )}

                {/* Botões */}
                <div className="flex items-center justify-end gap-3">
                    <button
                        type="button"
                        className="btn btn-ghost"
                        onClick={() => navigate('/registrations/establishments')}
                    >
                        Cancelar
                    </button>
                    <button
                        type="submit"
                        className="btn btn-secondary gap-2"
                        disabled={saving || uploadingLogo}
                    >
                        {saving || uploadingLogo ? (
                            <span className="loading loading-spinner loading-sm"></span>
                        ) : (
                            <MdSave className="text-xl" />
                        )}
                        {isEditing ? 'Salvar Alterações' : 'Criar Estabelecimento'}
                    </button>
                </div>
            </form>
            {logoCrop.cropDialog}
        </div>
    )
}
